import { Link } from "@tanstack/react-router";
import { isIndexableModel } from "@/lib/index-policy";

interface UsefulToolsProps {
  /** Index-policy inputs — the widget is hidden on any page we mark noindex. */
  makeSlug: string;
  latestYear: number | null;
  rowCount: number;
}

const LINKS = [
  {
    to: "/best" as const,
    title: "Rankings",
    body: "Best MPG, towing and value lists built from EPA test data.",
  },
  {
    to: "/tools" as const,
    title: "Calculators",
    body: "Fuel cost, payload, towing and gas-vs-EV ownership tools.",
  },
  {
    to: "/methodology" as const,
    title: "Methodology",
    body: "Which government datasets we use and how figures are verified.",
  },
];

/**
 * Compact internal-link block for vehicle pages. Rendered only when the host
 * page itself passes the index policy, so no noindex page links out to a
 * money page.
 */
export function UsefulTools({ makeSlug, latestYear, rowCount }: UsefulToolsProps) {
  if (!isIndexableModel({ makeSlug, latestYear, rowCount })) return null;

  return (
    <aside
      aria-labelledby="useful-tools-heading"
      className="mx-auto max-w-6xl px-4 pb-12 sm:px-6"
    >
      <div className="rounded-2xl border border-border bg-card p-5 shadow-soft sm:p-6">
        <h2
          id="useful-tools-heading"
          className="font-heading text-base font-semibold text-foreground"
        >
          Useful tools &amp; guides
        </h2>
        <div className="mt-4 grid gap-3 sm:grid-cols-3">
          {LINKS.map((l) => (
            <Link
              key={l.to}
              to={l.to}
              className="group rounded-xl border border-border bg-background p-4 transition-colors hover:border-primary"
            >
              <span className="block font-semibold text-foreground transition-colors group-hover:text-primary">
                {l.title}
              </span>
              <span className="mt-1 block text-sm leading-relaxed text-muted-foreground">
                {l.body}
              </span>
            </Link>
          ))}
        </div>
      </div>
    </aside>
  );
}
